import { AuthenticatedTemplate, UnauthenticatedTemplate, useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { useEffect, useRef, useState } from 'react';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { loginRequest } from './auth/msal';
import { addressWithoutLoginHint, linkLoginHint } from './auth/linkHint';
import { arrivalLoginHint, claimAutoSignIn, shouldSignInAutomatically, signedOutOnPurpose } from './auth/ssoMemory';
import { AccountMenu } from './components/AccountMenu';
import { PortfolioProvider } from './lib/portfolio-context';
import { FacilityDetail } from './pages/FacilityDetail';
import { Portfolio } from './pages/Portfolio';

/**
 * The chrome.
 *
 * NO KEYBOARD HINT. The header used to advertise the facility switcher as
 * "⌘K to jump". Westport is a ~99% Windows shop, so the ⌘ glyph named a key
 * that essentially nobody looking at this screen has — it read as an
 * instruction that could not be followed. The switcher itself is unchanged and
 * still opens on Ctrl+K (and ⌘K on a Mac); only the misleading label is gone.
 *
 * `right` is a slot rather than a fixed control because the account menu needs
 * the portfolio context, which does not exist before sign-in.
 */
function Chrome({ right }: { right?: React.ReactNode }) {
  return (
    <header className="chrome">
      <div className="lock">
        <span className="lw">WESTPORT PROPERTIES</span>
        <img className="lm-img" src="/assets/mark-white.png" alt="" />
        <span className="ld" />
        <span className="la">FacilityWISE</span>
        <span className="ld" />
        <span className="ls">Facility Information</span>
      </div>
      <div className="cr">{right}</div>
    </header>
  );
}

/** Neither the page carrying Entra's answer nor MSAL's hidden renewal frame
 *  ever starts a sign-in of its own. */
const onAuthResponseOrInFrame = (): boolean =>
  /[?#&](code|error)=/.test(window.location.hash + window.location.search) || window.self !== window.top;

/** Who the sign-in names: the link's own hint, else whoever last signed in here. */
const arrivalRequest = () => {
  const hint = arrivalLoginHint(linkLoginHint(window.location.search, window.location.hash));
  return { ...loginRequest, ...(hint ? { loginHint: hint } : {}) };
};

/** The Sign in a person presses: as the arrival — but after a CHOSEN Sign out
 *  only the link's own hint, never the last person, who chose to leave (on a
 *  shared PC that is somebody else, asked for the leaver's password). */
const buttonRequest = () => {
  const link = linkLoginHint(window.location.search, window.location.hash);
  const hint = signedOutOnPurpose() ? link : arrivalLoginHint(link);
  return { ...loginRequest, ...(hint ? { loginHint: hint } : {}) };
};

function SignIn() {
  const { instance, inProgress } = useMsal();

  // NEVER A SIGN-IN PAGE FOR SOMEONE WITH AN ENTRA SESSION (the platform rule,
  // Chauncey White 2026-10-23: "EVERY link emailed or sent or coded should
  // check to see if the user is logged in"). Go straight to Entra — with the
  // link's login_hint (the Launchpad's handshake), else whoever last signed in
  // on this browser — never after a chosen Sign out.
  //
  // The sessionStorage guard is not optional: if that redirect ever comes back
  // unauthenticated (session expired, account blocked), an unguarded effect
  // would send the user straight back out again, forever, on a production app.
  // It is claimed BEFORE leaving (and only when it reads back), so one failed
  // attempt falls through to the button below.
  //
  // Seeded during render so the button never flashes before the redirect leaves.
  const [redirecting, setRedirecting] = useState(() => shouldSignInAutomatically() && !onAuthResponseOrInFrame());
  const started = useRef(false);
  useEffect(() => {
    // Wait for MSAL to settle. <UnauthenticatedTemplate> renders this component
    // during 'startup' and 'handleRedirect' too — there is simply no account
    // yet — and calling loginRedirect in either phase throws
    // interaction_in_progress, which would burn the one-shot guard and leave
    // the person on the button with nothing explaining why.
    if (started.current || inProgress !== InteractionStatus.None) return;
    if (onAuthResponseOrInFrame() || !shouldSignInAutomatically() || !claimAutoSignIn()) {
      setRedirecting(false);
      return;
    }
    started.current = true;
    instance.loginRedirect(arrivalRequest()).catch((err: unknown) => {
      // The navigation never happened; the button below is still the way in.
      console.warn('[facilitywise] automatic sign-in redirect failed', err);
      setRedirecting(false);
    });
  }, [instance, inProgress]);

  return (
    <div className="fw-signin">
      <div className="fw-signin-in">
        <h1>FacilityWISE</h1>
        {redirecting ? (
          // A sign-in in progress, not a request to sign in.
          <p role="status">Signing you in…</p>
        ) : (
          <>
            <p>Everything known about a site, in one place.</p>
            <button className="btn btn-p" onClick={() => void instance.loginRedirect(buttonRequest())} type="button">
              Sign in with Microsoft
            </button>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Signed in: the link's ?login_hint has done its work. An address copied from
 * here into Teams must not carry THIS person's hint — the colleague who opens
 * it would be signed in as, or asked the password of, somebody else
 * (auth/linkHint.ts). Through the router, so the portfolio's filters never
 * write it back; the path, the hash and every other key stay.
 */
function SignedInAddress() {
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    const url = addressWithoutLoginHint(window.location.pathname, window.location.search, window.location.hash);
    if (url !== null) navigate(url, { replace: true, state: location.state });
    // Once, on arrival signed in.
  }, []);
  return null;
}

export function App() {
  return (
    // The provider wraps the chrome as well as the routes, because the account
    // menu reports how old the projection is and that number lives in the
    // portfolio payload. It holds off fetching until MSAL has an account, so
    // hoisting it here costs no unauthenticated request.
    <PortfolioProvider>
      {/* OUTSIDE both MSAL templates on purpose. Both render nothing while
          `inProgress` is anything but `none` — which includes the redirect
          handling on every return from Entra — so a header nested inside them
          leaves a completely blank white page during sign-in. */}
      <Chrome
        right={
          <AuthenticatedTemplate>
            <AccountMenu />
          </AuthenticatedTemplate>
        }
      />
      <div className="screen">
        <UnauthenticatedTemplate>
          <SignIn />
        </UnauthenticatedTemplate>
        <AuthenticatedTemplate>
          <SignedInAddress />
          <Routes>
            <Route path="/" element={<Portfolio />} />
            <Route path="/facility/:id" element={<FacilityDetail />} />
            <Route path="*" element={<Portfolio />} />
          </Routes>
        </AuthenticatedTemplate>
      </div>
    </PortfolioProvider>
  );
}
